/**
 * goToTextareaBegin.js
 * ---------------------
 * ES6 Module to ensure that textareas automatically move cursor to the beginning
 * when clicked or focused. Supports multiple configuration options.
 *
 * Features:
 * - Can target specific elements by ID, class, or selector
 * - Can auto-initialize for common textarea IDs
 * - Supports manual initialization for dynamic content
 * - Uses a shared ErrorHandler for critical failure tracking
 *
 * Usage Examples:
 * - Auto-init: Just import the module (targets common IDs like 'message')
 * - Manual: initializeTextarea('my-textarea-id')
 * - Selector: initializeTextareas('.my-textarea-class')
 * - Element: initializeTextareaElement(textareaElement)
 *
 * Error Handling:
 * - Instantiates ErrorHandler with fallback no-op stub on failure
 * - Logs initialization failures via `handleFatalError`
 * - Exposes diagnostics: getRecentErrors, hasCriticalIssues, getErrorStats
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Initialize a shared error handler (with fallback)
let errorHandler;
try {
  errorHandler = new ErrorHandler({ module: 'goToTextareaBegin', maxErrors: 10 });
} catch (e) {
  console.warn('goToTextareaBegin: ErrorHandler not available, using fallback', e);
  errorHandler = {
    handleFatalError: () => {},
    handleError: () => {},
    logInfo: () => {},
    getRecentErrors: () => [],
    hasFatalErrors: () => false,
    getErrorStats: () => ({ totalErrors: 0, fatalErrors: 0 })
  };
}

// Track initialized textareas to avoid duplicate handlers
const initializedTextareas = new WeakSet();

// Default textarea IDs to auto-initialize
const DEFAULT_TEXTAREA_IDS = ['message', 'comment', 'description', 'content'];

/**
 * Create the cursor-to-start handler function
 * @param {HTMLTextAreaElement} textarea - The textarea element
 * @returns {Function} The event handler function
 */
function createCursorHandler(textarea) {
  return function moveCursorToStart() {
    try {
      textarea.setSelectionRange(0, 0);
    } catch (error) {
      errorHandler.handleError('Error moving cursor to start', error, {
        category: 'cursor-management',
        textareaId: textarea.id || 'unknown'
      });
    }
  };
}

/**
 * Initialize cursor management for a single textarea element
 * @param {HTMLTextAreaElement} textarea - The textarea element
 * @returns {boolean} True if successfully initialized
 */
export function initializeTextareaElement(textarea) {
  if (!textarea || textarea.tagName !== 'TEXTAREA') {
    console.warn('goToTextareaBegin: Invalid textarea element provided');
    return false;
  }

  // Skip if already initialized
  if (initializedTextareas.has(textarea)) {
    errorHandler.logInfo(`Textarea already initialized: ${textarea.id || 'unnamed'}`);
    return true;
  }

  try {
    const handler = createCursorHandler(textarea);
    textarea.addEventListener('focus', handler);
    textarea.addEventListener('click', handler);
    
    // Mark as initialized
    initializedTextareas.add(textarea);
    
    errorHandler.logInfo(`Textarea cursor management initialized: ${textarea.id || 'unnamed'}`);
    return true;
  } catch (error) {
    errorHandler.handleError(
      'Failed to initialize textarea cursor management',
      error,
      { 
        category: 'initialization',
        textareaId: textarea.id || 'unknown'
      }
    );
    return false;
  }
}

/**
 * Initialize cursor management for a textarea by ID
 * @param {string} textareaId - The ID of the textarea
 * @param {Object} options - Configuration options
 * @param {boolean} options.silent - If true, suppress warnings for missing textareas
 * @returns {boolean} True if successfully initialized
 */
export function initializeTextarea(textareaId, options = {}) {
  const { silent = false } = options;

  if (!isBrowser) {
    if (!silent) console.warn('goToTextareaBegin: Not in browser environment');
    return false;
  }

  if (!textareaId || typeof textareaId !== 'string') {
    if (!silent) console.warn('goToTextareaBegin: Invalid textarea ID provided');
    return false;
  }

  const textarea = document.getElementById(textareaId);
  if (!textarea) {
    // Only warn if not in silent mode (auto-init is silent)
    if (!silent) {
      console.warn(`goToTextareaBegin: textarea with ID "${textareaId}" not found`);
    }
    return false;
  }

  return initializeTextareaElement(textarea);
}

/**
 * Initialize cursor management for multiple textareas using a CSS selector
 * @param {string} selector - CSS selector to find textareas
 * @returns {number} Number of textareas successfully initialized
 */
export function initializeTextareas(selector) {
  if (!isBrowser) {
    console.warn('goToTextareaBegin: Not in browser environment');
    return 0;
  }

  if (!selector || typeof selector !== 'string') {
    console.warn('goToTextareaBegin: Invalid selector provided');
    return 0;
  }

  try {
    const textareas = document.querySelectorAll(selector);
    let initCount = 0;

    textareas.forEach(textarea => {
      if (textarea.tagName === 'TEXTAREA' && initializeTextareaElement(textarea)) {
        initCount++;
      }
    });

    errorHandler.logInfo(`Initialized ${initCount} textareas with selector: ${selector}`);
    return initCount;
  } catch (error) {
    errorHandler.handleError(
      'Failed to initialize textareas with selector',
      error,
      { 
        category: 'initialization',
        selector: selector
      }
    );
    return 0;
  }
}

/**
 * Auto-initialize common textarea IDs
 * Runs silently - only initializes textareas that exist on the page
 */
function autoInitializeCommonTextareas() {
  let totalInitialized = 0;

  // Use silent mode for auto-initialization to avoid warnings on pages
  // that don't have all the default textareas (e.g., events/training, services pages)
  DEFAULT_TEXTAREA_IDS.forEach(id => {
    if (initializeTextarea(id, { silent: true })) {
      totalInitialized++;
    }
  });

  if (totalInitialized > 0) {
    errorHandler.logInfo(`Auto-initialized ${totalInitialized} common textareas`);
  }

  return totalInitialized;
}

/**
 * Initialize all textareas on the page
 * @returns {number} Number of textareas initialized
 */
export function initializeAllTextareas() {
  return initializeTextareas('textarea');
}

/**
 * Remove cursor management from a textarea element
 * @param {HTMLTextAreaElement} textarea - The textarea element
 * @returns {boolean} True if successfully removed
 */
export function removeTextareaHandler(textarea) {
  if (!textarea || textarea.tagName !== 'TEXTAREA') {
    return false;
  }

  try {
    // We can't remove the specific handler since we don't store references,
    // but we can mark it as uninitialized so it can be re-initialized if needed
    initializedTextareas.delete(textarea);
    errorHandler.logInfo(`Textarea handler removed: ${textarea.id || 'unnamed'}`);
    return true;
  } catch (error) {
    errorHandler.handleError('Failed to remove textarea handler', error, {
      category: 'cleanup',
      textareaId: textarea.id || 'unknown'
    });
    return false;
  }
}

/**
 * Get status of initialized textareas
 * @returns {Object} Status object with counts and details
 */
export function getInitializationStatus() {
  const allTextareas = isBrowser ? document.querySelectorAll('textarea') : [];
  const initialized = Array.from(allTextareas).filter(ta => initializedTextareas.has(ta));
  
  return {
    totalTextareas: allTextareas.length,
    initializedCount: initialized.length,
    uninitializedCount: allTextareas.length - initialized.length,
    initializedIds: initialized.map(ta => ta.id || 'unnamed'),
    errorStats: errorHandler.getErrorStats()
  };
}

/**
 * Main initialization function
 * Runs silently on all pages - only initializes textareas that exist
 */
function initializeTextareaCursor() {
  try {
    const initialized = autoInitializeCommonTextareas();

    // Removed warning for zero initialized - this is expected on pages without textareas
    // The function runs on all pages that load the contact system, but not all pages
    // have contact forms with textareas (e.g., events/training, services landing pages)
  } catch (error) {
    errorHandler.handleFatalError(
      'Critical error during auto-initialization',
      error,
      { category: 'initialization' }
    );
    console.error('💥 Critical error during textarea auto-initialization:', error);
  }
}

// Auto-initialize when module loads (browser environment only)
if (isBrowser) {
  // Check if DOM is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTextareaCursor);
  } else {
    // DOM is already ready, initialize immediately
    initializeTextareaCursor();
  }
}

/**
 * Get recent errors from initialization or handlers.
 * @param {number} minutes
 */
export function getRecentErrors(minutes = 5) {
  return errorHandler.getRecentErrors(minutes);
}

/**
 * Whether any fatal issues occurred.
 */
export function hasCriticalIssues() {
  return errorHandler.hasFatalErrors();
}

/**
 * Retrieve collected error stats.
 */
export function getErrorStats() {
  return errorHandler.getErrorStats();
}